import React, { useState } from 'react';
import { SessionData, Trial } from '../types';

interface DataInputProps {
  onDataSubmit: (data: SessionData) => void;
  initialData?: SessionData;
}

export const DataInput: React.FC<DataInputProps> = ({ onDataSubmit, initialData }) => {
  const [athlete, setAthlete] = useState(initialData?.athlete || {
    name: '',
    massKg: 75,
    sport: 'General',
    position: ''
  });

  const [session, setSession] = useState(initialData?.session || {
    date: new Date().toISOString().split('T')[0],
    v_free_ms: 9.0,
    sprint_distance_m: 30,
    trials: [
      { load_kgf: 0, v_max_ms: 9.0 },
      { load_kgf: 10, v_max_ms: 7.5 },
      { load_kgf: 20, v_max_ms: 6.0 },
      { load_kgf: 30, v_max_ms: 4.5 }
    ]
  });

  const [refs] = useState(initialData?.refs || {
    sport: 'General',
    F0_ref_Nkg: 7.5,
    V0_ref_ms: 9.5
  });

  const handleTrialChange = (index: number, field: keyof Trial, value: number | undefined) => {
    const newTrials = [...session.trials];
    if (value === undefined) {
      const { [field]: removed, ...rest } = newTrials[index];
      newTrials[index] = rest as Trial;
    } else {
      newTrials[index] = { ...newTrials[index], [field]: value };
    }
    setSession({ ...session, trials: newTrials });
  };

  const addTrial = () => {
    setSession({
      ...session,
      trials: [...session.trials, { load_kgf: 0, v_max_ms: 0 }]
    });
  };

  const removeTrial = (index: number) => {
    if (session.trials.length > 2) {
      const newTrials = session.trials.filter((_, i) => i !== index);
      setSession({ ...session, trials: newTrials });
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onDataSubmit({ athlete, session, refs });
  };

  return (
    <div className="bg-theme-surface-1 border border-theme-border rounded-2xl p-6 shadow-card">
      <h3 className="text-2xl font-semibold tracking-tight text-theme-text mb-6">Session Data Input</h3>
      
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Athlete Info */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-base font-medium text-theme-text mb-2">
              Athlete Name
            </label>
            <input
              type="text"
              value={athlete.name}
              onChange={(e) => setAthlete({ ...athlete, name: e.target.value })}
              className="w-full px-3 py-2 bg-theme-surface-1 border border-theme-border rounded-lg text-theme-text placeholder:text-theme-muted hover:border-slate-500 focus:outline-none focus:ring-2 focus:ring-theme-focus transition-colors"
              required
            />
          </div>
          <div>
            <label className="block text-base font-medium text-theme-text mb-2">
              Mass (kg)
            </label>
            <input
              type="number"
              step="0.1"
              value={athlete.massKg}
              onChange={(e) => setAthlete({ ...athlete, massKg: parseFloat(e.target.value) })}
              className="w-full px-3 py-2 bg-theme-surface-1 border border-theme-border rounded-lg text-theme-text placeholder:text-theme-muted hover:border-slate-500 focus:outline-none focus:ring-2 focus:ring-theme-focus transition-colors"
              required
            />
          </div>
          <div>
            <label className="block text-base font-medium text-theme-text mb-2">
              Sport
            </label>
            <input
              type="text"
              value={athlete.sport}
              onChange={(e) => setAthlete({ ...athlete, sport: e.target.value })}
              className="w-full px-3 py-2 bg-theme-surface-1 border border-theme-border rounded-lg text-theme-text placeholder:text-theme-muted hover:border-slate-500 focus:outline-none focus:ring-2 focus:ring-theme-focus transition-colors"
            />
          </div>
          <div>
            <label className="block text-base font-medium text-theme-text mb-2">
              Position
            </label>
            <input
              type="text"
              value={athlete.position}
              onChange={(e) => setAthlete({ ...athlete, position: e.target.value })}
              className="w-full px-3 py-2 bg-theme-surface-1 border border-theme-border rounded-lg text-theme-text placeholder:text-theme-muted hover:border-slate-500 focus:outline-none focus:ring-2 focus:ring-theme-focus transition-colors"
            />
          </div>
        </div>

        {/* Session Info */}
        <div className="grid grid-cols-3 gap-4">
          <div>
            <label className="block text-base font-medium text-theme-text mb-2">
              Date
            </label>
            <input
              type="date"
              value={session.date}
              onChange={(e) => setSession({ ...session, date: e.target.value })}
              className="w-full px-3 py-2 bg-theme-surface-1 border border-theme-border rounded-lg text-theme-text placeholder:text-theme-muted hover:border-slate-500 focus:outline-none focus:ring-2 focus:ring-theme-focus transition-colors"
              required
            />
          </div>
          <div>
            <label className="block text-base font-medium text-theme-text mb-2">
              Sprint Distance (m)
            </label>
            <input
              type="number"
              step="1"
              value={session.sprint_distance_m}
              onChange={(e) => setSession({ ...session, sprint_distance_m: parseFloat(e.target.value) })}
              className="w-full px-3 py-2 bg-theme-surface-1 border border-theme-border rounded-lg text-theme-text placeholder:text-theme-muted hover:border-slate-500 focus:outline-none focus:ring-2 focus:ring-theme-focus transition-colors"
              required
            />
          </div>
          <div>
            <label className="block text-base font-medium text-theme-text mb-2">
              Free Sprint Speed (m/s)
            </label>
            <input
              type="number"
              step="0.1"
              value={session.v_free_ms}
              onChange={(e) => setSession({ ...session, v_free_ms: parseFloat(e.target.value) })}
              className="w-full px-3 py-2 bg-theme-surface-1 border border-theme-border rounded-lg text-theme-text placeholder:text-theme-muted hover:border-slate-500 focus:outline-none focus:ring-2 focus:ring-theme-focus transition-colors"
              required
            />
          </div>
        </div>

        {/* Trials */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-xl font-semibold tracking-tight text-theme-text">Sprint Trials</h4>
            <button
              type="button"
              onClick={addTrial}
              className="px-4 py-2 bg-theme-surface-2 text-theme-text hover:bg-slate-600 active:bg-slate-500 border border-theme-border rounded-lg font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-theme-focus"
            >
              Add Trial
            </button>
          </div>
          
          <div className="space-y-3">
            {session.trials.map((trial, index) => (
              <div key={index} className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-theme-surface-2 border border-theme-border rounded-lg">
                <div className="flex-1">
                  <label className="block text-sm font-medium text-theme-text mb-1">Load (kgf)</label>
                  <input
                    type="number"
                    step="0.1"
                    value={trial.load_kgf}
                    onChange={(e) => handleTrialChange(index, 'load_kgf', parseFloat(e.target.value))}
                    className="w-full px-3 py-2 bg-theme-surface-1 border border-theme-border rounded-lg text-theme-text placeholder:text-theme-muted hover:border-slate-500 focus:outline-none focus:ring-2 focus:ring-theme-focus transition-colors"
                  />
                </div>
                <div className="flex-1">
                  <label className="block text-sm font-medium text-theme-text mb-1">Max Velocity (m/s)</label>
                  <input
                    type="number"
                    step="0.1"
                    value={trial.v_max_ms}
                    onChange={(e) => handleTrialChange(index, 'v_max_ms', parseFloat(e.target.value))}
                    className="w-full px-3 py-2 bg-theme-surface-1 border border-theme-border rounded-lg text-theme-text placeholder:text-theme-muted hover:border-slate-500 focus:outline-none focus:ring-2 focus:ring-theme-focus transition-colors"
                  />
                </div>
                <div className="flex-1">
                  <label className="block text-sm font-medium text-theme-text mb-1">Power (W) <span className="text-theme-muted text-xs">optional</span></label>
                  <input
                    type="number"
                    step="1"
                    value={trial.power_W || ''}
                    onChange={(e) => handleTrialChange(index, 'power_W', e.target.value ? parseFloat(e.target.value) : undefined)}
                    className="w-full px-3 py-2 bg-theme-surface-1 border border-theme-border rounded-lg text-theme-text placeholder:text-theme-muted hover:border-slate-500 focus:outline-none focus:ring-2 focus:ring-theme-focus transition-colors"
                    placeholder="Optional"
                  />
                </div>
                <div className="flex-1">
                  <label className="block text-sm font-medium text-theme-text mb-1">Sprint Time (s) <span className="text-theme-muted text-xs">optional</span></label>
                  <input
                    type="number"
                    step="0.01"
                    value={trial.sprint_time_s || ''}
                    onChange={(e) => handleTrialChange(index, 'sprint_time_s', e.target.value ? parseFloat(e.target.value) : undefined)}
                    className="w-full px-3 py-2 bg-theme-surface-1 border border-theme-border rounded-lg text-theme-text placeholder:text-theme-muted hover:border-slate-500 focus:outline-none focus:ring-2 focus:ring-theme-focus transition-colors"
                    placeholder="Optional"
                  />
                </div>
                {session.trials.length > 2 && (
                  <button
                    type="button"
                    onClick={() => removeTrial(index)}
                    className="text-red-500 hover:text-red-400 text-xl font-bold w-8 h-8 flex items-center justify-center rounded hover:bg-red-950/20 transition-colors"
                  >
                    ×
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>

        <button
          type="submit"
          className="w-full bg-white text-theme-text-inverse py-3 px-6 rounded-lg hover:bg-slate-100 active:bg-slate-200 font-semibold text-lg transition-colors focus:outline-none focus:ring-2 focus:ring-theme-focus"
        >
          Analyze Performance
        </button>
      </form>
    </div>
  );
};